import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { UniformSeasonService } from '../instances/uniform-season';
import { UniformSeasonConverter } from '@/converters';

export const useUniformSeason = () => {
  const query = useQuery(
    ['GET_UNIFORM_SEASON'],
    async () => {
      const [err, data] = await UniformSeasonService.getUniformSeasons();
      if (err) {
        throw err;
      }
      return UniformSeasonConverter.toDomainFromGetResponse(data);
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useUniformSeasonById = (id: string) => {
  const query = useQuery(
    ['GET_UNIFORM_SEASON_BY_ID', id],
    async () => {
      const [err, data] = await UniformSeasonService.getUniformSeasonById(id);
      if (err) {
        throw err;
      }
      return UniformSeasonConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostUniformSeasonMutation = () => {
  return useMutation({
    mutationKey: ['POST_UNIFORM_SEASON'],
    mutationFn: async (payload: any) => {
      const [err, data] = await UniformSeasonService.postUniformSeason(payload);
      if (err) {
        throw err;
      }
      return UniformSeasonConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutUniformSeasonMutation = () => {
  return useMutation({
    mutationKey: ['PUT_UNIFORM_SEASON'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await UniformSeasonService.putUniformSeason(id, payload);
      if (err) {
        throw err;
      }
      return UniformSeasonConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteUniformSeasonMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_UNIFORM_SEASON'],
    mutationFn: async (id: string) => {
      const [err] = await UniformSeasonService.deleteUniformSeason(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};
